import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '@/components/common/SafeIcon'
import MidnightClock from '@/components/common/MidnightClock'
import LegalFooter from '@/components/common/LegalFooter'
import { seats } from '@/data/characters'
import { useAuthContext } from '@/components/auth/AuthProvider'

const { FiArrowRight, FiArrowLeft, FiLogOut, FiUser, FiDollarSign } = FiIcons

export default function Bar() {
  const navigate = useNavigate()
  const { user } = useAuthContext()
  const [hoveredSeat, setHoveredSeat] = useState(null)

  return (
    <div className="min-h-screen min-h-[100dvh] bg-[#0a0a0c] relative overflow-x-hidden">
      <div className="grain-overlay" />

      <AnimatePresence>
        {hoveredSeat !== null && (
          <motion.div
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 0.2, scale: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-0 pointer-events-none"
          >
            <img
              src={seats[hoveredSeat].img}
              className="w-full h-full object-cover filter blur-[8px] contrast-125 brightness-75 sepia-[0.3] hue-rotate-[-15deg]"
              alt=""
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0c] via-[#0a0a0c]/90 to-[#0a0a0c]/60" />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="vignette z-10" />

      {/* 頂部導航列 */}
      <nav className="sticky top-0 z-50 px-5 py-5 md:px-10 md:py-7 flex justify-between items-center bg-gradient-to-b from-[#0a0a0c]/95 via-[#0a0a0c]/70 to-transparent backdrop-blur-sm">
        <div className="group relative">
          <div className="flex flex-col space-y-1">
            <span className="text-amber-500/80 font-serif tracking-[0.2em] md:tracking-[0.3em] text-xs md:text-sm whitespace-nowrap">
              Midnight Bar
            </span>
            <span className="text-gray-400 font-serif text-[8px] md:text-[9px] tracking-[0.4em] uppercase whitespace-nowrap">
              Midnight Sanctum
            </span>
          </div>
        </div>

        <div className="flex items-center space-x-3 md:space-x-5">
          <button
            onClick={() => navigate('/pricing')}
            className="group flex items-center space-x-2 bg-white/[0.03] border border-amber-500/30 hover:border-amber-500/60 hover:bg-amber-500/10 px-3 md:px-4 py-1.5 md:py-2 rounded-full backdrop-blur-md transition-all duration-300"
          >
            <SafeIcon icon={FiDollarSign} className="text-amber-400 text-sm md:text-base" />
            <span className="text-amber-400 text-xs md:text-sm tracking-wider hidden sm:block">定價</span>
          </button>

          <button
            onClick={() => navigate('/profile')}
            className="group flex items-center space-x-2.5 bg-white/[0.03] border border-white/10 hover:border-amber-500/40 hover:bg-amber-500/10 px-3 md:px-4 py-1.5 md:py-2 rounded-full backdrop-blur-md transition-all duration-300"
          >
            <div className="w-5 h-5 md:w-6 md:h-6 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center">
              <SafeIcon icon={FiUser} className="text-white text-[10px] md:text-xs" />
            </div>
            <span className="text-[10px] md:text-xs text-gray-300 group-hover:text-amber-400 tracking-[0.2em] hidden sm:block transition-colors">
              {user?.name || '旅人'}
            </span>
          </button>

          <MidnightClock />
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-5 md:px-8 w-full pb-32 relative z-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-12 md:mb-16 flex flex-col items-center text-center pt-8 md:pt-12"
        >
          <p className="text-amber-400 font-light tracking-[0.4em] text-[9px] md:text-[10px] mb-3 md:mb-4 uppercase">
            Inside the Bar
          </p>
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-serif text-white/90 tracking-[0.1em] font-light leading-relaxed">
            店裡很安靜，<br className="md:hidden" />你想坐在哪裡?
          </h2>
          <div className="w-8 md:w-12 h-[1px] bg-amber-500/50 mt-6 md:mt-8" />
        </motion.div>

        <div className="flex flex-col space-y-5 md:space-y-8">
          {seats.map((seat, index) => {
            const isReversed = index % 2 !== 0
            return (
              <motion.div
                key={seat.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.15 }}
                onMouseEnter={() => setHoveredSeat(index)}
                onMouseLeave={() => setHoveredSeat(null)}
                onClick={() => navigate(`/chat/${seat.id}`)}
                className="group cursor-pointer relative overflow-hidden rounded-2xl md:rounded-3xl border border-white/[0.03] hover:border-amber-500/30 transition-all duration-700 bg-white/[0.01]"
              >
                <div className="absolute inset-0 z-0">
                  <img
                    src={seat.img}
                    className="w-full h-full object-cover opacity-10 group-hover:opacity-30 filter grayscale group-hover:grayscale-0 group-hover:contrast-125 group-hover:scale-105 transition-all duration-1000"
                    alt=""
                  />
                  <div className="absolute inset-0 bg-[#0a0a0c]/80 group-hover:bg-transparent transition-colors duration-1000" />
                  <div className={`absolute inset-0 bg-gradient-to-${isReversed ? 'l' : 'r'} from-[#0a0a0c] via-[#0a0a0c]/90 to-transparent`} />
                </div>

                <div className={`relative z-10 p-6 sm:p-8 md:p-12 lg:p-14 flex flex-col ${isReversed ? 'md:flex-row-reverse text-right' : 'md:flex-row text-left'} md:items-center justify-between`}>
                  <div className="max-w-[280px] sm:max-w-sm md:max-w-md">
                    <span className="block text-[8px] md:text-[9px] tracking-[0.5em] text-amber-500/80 mb-2 md:mb-3 uppercase group-hover:text-amber-400 transition-colors">
                      {seat.title}
                    </span>
                    <h3 className="text-xl sm:text-2xl md:text-3xl font-serif text-white/90 group-hover:text-white transition-colors mb-2 md:mb-4">
                      {seat.name}
                    </h3>
                    <p className="text-gray-400 text-xs md:text-sm font-light leading-relaxed tracking-wide group-hover:text-gray-200 transition-colors line-clamp-2 md:line-clamp-none">
                      {seat.desc}
                    </p>
                  </div>
                  <div className="hidden md:flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full border border-white/10 group-hover:border-amber-500/50 text-white/20 group-hover:text-amber-400 transition-all">
                    <SafeIcon icon={isReversed ? FiArrowLeft : FiArrowRight} className="text-base md:text-lg" />
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>

        <LegalFooter className="mt-16 md:mt-20 opacity-40 hover:opacity-100 transition-opacity" />
      </div>

      {/* 底部推門離開按鈕 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
        className="fixed bottom-6 md:bottom-10 left-1/2 -translate-x-1/2 z-50"
      >
        <button
          onClick={() => navigate('/')}
          className="group flex items-center space-x-3 px-6 py-3 bg-[#0a0a0c]/80 border border-white/10 hover:border-amber-500/40 rounded-full backdrop-blur-md transition-all duration-300 shadow-[0_10px_30px_rgba(0,0,0,0.5)] hover:shadow-[0_0_20px_rgba(245,158,11,0.2)] hover:-translate-y-1"
        >
          <SafeIcon icon={FiLogOut} className="text-gray-400 group-hover:text-amber-400 transition-colors text-sm" />
          <span className="text-gray-400 group-hover:text-amber-400 text-[10px] md:text-xs tracking-[0.4em] uppercase transition-colors">推門離開</span>
        </button>
      </motion.div>
    </div>
  )
}